import { Injectable } from '@angular/core';
import { Season, SeasonPreset, SEASON_PRESETS, SEASON_COLORS, UnitSeasonPrice } from '../models';

export interface PresetCoverageItem {
  preset: SeasonPreset;
  year: number;
  startDate: Date;
  endDate: Date;
  covered: boolean;
  coveringSeasonName?: string;
}

export interface YearPresetGroup {
  year: number;
  items: PresetCoverageItem[];
  coveredCount: number;
  totalCount: number;
}

@Injectable()
export class SeasonCalculatorService {

  getPresets(): SeasonPreset[] {
    return SEASON_PRESETS;
  }

  getAvailableColors(existingSeasons: Season[]): string[] {
    const usedColors = existingSeasons.map(s => s.color);
    return SEASON_COLORS.filter(c => !usedColors.includes(c));
  }

  getNextColor(existingSeasons: Season[]): string {
    const available = this.getAvailableColors(existingSeasons);
    return available.length > 0 ? available[0] : SEASON_COLORS[0];
  }

  applyPreset(preset: SeasonPreset, year: number): { startDate: Date; endDate: Date } {
    return preset.getDateRange(year);
  }

  createSeasonFromPreset(
    preset: SeasonPreset,
    year: number,
    unitTypeIds: string[],
    existingSeasons: Season[]
  ): Season {
    const dateRange = this.applyPreset(preset, year);
    const id = this.generateId();

    return {
      id,
      name: preset.name,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      color: this.getNextColor(existingSeasons),
      unitPrices: unitTypeIds.map(unitTypeId => ({
        unitTypeId,
        pricePerNight: 0
      }))
    };
  }

  createEmptySeason(unitTypeIds: string[], existingSeasons: Season[]): Season {
    return {
      id: this.generateId(),
      name: '',
      startDate: new Date(),
      endDate: new Date(),
      color: this.getNextColor(existingSeasons),
      unitPrices: unitTypeIds.map(unitTypeId => ({
        unitTypeId,
        pricePerNight: 0
      }))
    };
  }

  findOverlappingSeasons(newSeason: Season, existingSeasons: Season[]): Season[] {
    return existingSeasons.filter(existing => {
      if (existing.id === newSeason.id) return false;
      return this.doDateRangesOverlap(
        newSeason.startDate, newSeason.endDate,
        existing.startDate, existing.endDate
      );
    });
  }

  doDateRangesOverlap(start1: Date, end1: Date, start2: Date, end2: Date): boolean {
    return start1 <= end2 && start2 <= end1;
  }

  getSeasonDuration(season: Season): number {
    if (!season.startDate || !season.endDate) return 0;
    const diffTime = season.endDate.getTime() - season.startDate.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  }

  formatDateRange(season: Season): string {
    if (!season.startDate || !season.endDate) return '';
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
    const start = season.startDate.toLocaleDateString('pl-PL', options);
    const end = season.endDate.toLocaleDateString('pl-PL', options);
    return `${start} - ${end}`;
  }

  syncUnitPrices(season: Season, currentUnitTypeIds: string[]): Season {
    const existingPriceMap = new Map(
      season.unitPrices.map(up => [up.unitTypeId, up.pricePerNight])
    );

    const updatedPrices: UnitSeasonPrice[] = currentUnitTypeIds.map(unitTypeId => ({
      unitTypeId,
      pricePerNight: existingPriceMap.get(unitTypeId) ?? 0
    }));

    return {
      ...season,
      unitPrices: updatedPrices
    };
  }

  getCurrentYear(): number {
    return new Date().getFullYear();
  }

  getYearOptions(): number[] {
    const currentYear = this.getCurrentYear();
    return [currentYear, currentYear + 1, currentYear + 2];
  }

  /**
   * Check if a preset's date range for a given year is covered by any existing season.
   * "Covered" = at least one existing season overlaps the preset range.
   */
  isPresetCovered(preset: SeasonPreset, year: number, existingSeasons: Season[]): { covered: boolean; coveringSeasonName?: string } {
    const { startDate, endDate } = preset.getDateRange(year);
    for (const season of existingSeasons) {
      if (this.doDateRangesOverlap(startDate, endDate, season.startDate, season.endDate)) {
        return { covered: true, coveringSeasonName: season.name };
      }
    }
    return { covered: false };
  }

  /**
   * Get all presets with coverage status, grouped by year.
   */
  getPresetsWithCoverage(existingSeasons: Season[]): YearPresetGroup[] {
    const years = this.getYearOptions();
    const presets = this.getPresets();

    return years.map(year => {
      const items: PresetCoverageItem[] = presets.map(preset => {
        const { startDate, endDate } = preset.getDateRange(year);
        const coverage = this.isPresetCovered(preset, year, existingSeasons);
        return {
          preset,
          year,
          startDate,
          endDate,
          covered: coverage.covered,
          coveringSeasonName: coverage.coveringSeasonName,
        };
      });

      return {
        year,
        items,
        coveredCount: items.filter(i => i.covered).length,
        totalCount: items.length,
      };
    });
  }

  formatDate(date: Date): string {
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit' };
    return date.toLocaleDateString('pl-PL', options);
  }

  private generateId(): string {
    return `season_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }
}
